import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import type { Profile, ModelingStyle, LanguageCode } from '@modelcast/types';
import { LANGUAGES } from '@modelcast/types';
import { NavBar } from '../../components/NavBar';
import { SharedFooter } from '../../components/SharedFooter';
import { COUNTRIES } from '../model-signup/countries';
import { MODELING_STYLE_OPTIONS } from '../model-signup/modelSignup.types';
import { FollowButton } from '../follow/FollowButton';

// Country flag emoji from country code
function getCountryFlag(code: string): string {
  const codePoints = code
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
}

// Get country name from code
function getCountryName(code: string): string {
  return COUNTRIES.find(c => c.code === code)?.name ?? code;
}

// Group countries by region
const REGIONS: Record<string, string[]> = {
  'Europe': ['GB', 'FR', 'DE', 'ES', 'IT', 'NL', 'BE', 'PT', 'SE', 'NO', 'DK', 'FI', 'IE', 'AT', 'CH', 'PL', 'CZ', 'GR', 'RS', 'HR', 'RO', 'BG', 'HU', 'UA', 'GE'],
  'North America': ['US', 'CA', 'MX'],
  'Asia & Pacific': ['JP', 'KR', 'CN', 'TW', 'HK', 'SG', 'AU', 'NZ', 'IN', 'TH', 'VN', 'PH', 'ID', 'MY'],
  'South America': ['BR', 'AR', 'CL', 'CO', 'PE'],
  'Africa & Middle East': ['ZA', 'NG', 'KE', 'EG', 'IL', 'AE', 'SA'],
};

function getRegion(countryCode: string | null): string | null {
  if (!countryCode) return null;
  for (const [region, codes] of Object.entries(REGIONS)) {
    if (codes.includes(countryCode)) return region;
  }
  return 'Other';
}

interface ModelWithCount extends Profile {
  sessionCount?: number;
}

export function ModelsDirectoryPage() {
  const [models, setModels] = useState<ModelWithCount[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sort, setSort] = useState<'featured' | 'recent' | 'alphabetical'>('featured');
  const [filters, setFilters] = useState<{
    regions: Set<string>;
    styles: Set<ModelingStyle>;
    languages: Set<LanguageCode>;
  }>({
    regions: new Set(),
    styles: new Set(),
    languages: new Set(),
  });

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Fetch models
  useEffect(() => {
    fetchModels();
  }, [debouncedSearch, sort]);

  const fetchModels = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (sort === 'recent') params.set('sort', 'recent');
      if (sort === 'alphabetical') params.set('sort', 'alphabetical');
      params.set('limit', '100');

      const response = await fetch(`/api/models?${params.toString()}`);
      if (response.ok) {
        const data = await response.json();
        setModels(data.models);
        setTotal(data.total);
      }
    } catch (error) {
      console.error('Error fetching models:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter models client-side for regions and styles
  const filteredModels = useMemo(() => {
    return models.filter(model => {
      // Region filter
      if (filters.regions.size > 0) {
        const region = getRegion(model.country);
        if (!region || !filters.regions.has(region)) return false;
      }

      // Style filter
      if (filters.styles.size > 0) {
        const modelStyles = new Set(model.modelingStyles || []);
        let hasMatch = false;
        for (const style of filters.styles) {
          if (modelStyles.has(style)) hasMatch = true;
        }
        if (!hasMatch) return false;
      }

      // Language filter
      if (filters.languages.size > 0) {
        const modelLangs = new Set(model.languages || []);
        let hasMatch = false;
        for (const lang of filters.languages) {
          if (modelLangs.has(lang)) hasMatch = true;
        }
        if (!hasMatch) return false;
      }

      return true;
    });
  }, [models, filters]);

  // Sort models
  const sortedModels = useMemo(() => {
    const sorted = [...filteredModels];
    if (sort === 'alphabetical') {
      sorted.sort((a, b) => a.displayName.localeCompare(b.displayName));
    }
    // For 'featured' and 'recent', API handles the sort
    return sorted;
  }, [filteredModels, sort]);

  // Calculate region counts
  const regionCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const model of models) {
      const region = getRegion(model.country);
      if (region) {
        counts[region] = (counts[region] || 0) + 1;
      }
    }
    return counts;
  }, [models]);

  // Calculate style counts
  const styleCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const model of models) {
      for (const style of (model.modelingStyles || [])) {
        counts[style] = (counts[style] || 0) + 1;
      }
    }
    return counts;
  }, [models]);

  // Calculate language counts
  const languageCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const model of models) {
      for (const lang of (model.languages || [])) {
        counts[lang] = (counts[lang] || 0) + 1;
      }
    }
    return counts;
  }, [models]);

  const toggleFilter = (type: 'regions' | 'styles' | 'languages', value: string) => {
    setFilters(prev => {
      const newSet = new Set(prev[type]);
      if (newSet.has(value)) {
        newSet.delete(value);
      } else {
        newSet.add(value);
      }
      return { ...prev, [type]: newSet };
    });
  };

  const clearFilters = () => {
    setFilters({ regions: new Set(), styles: new Set(), languages: new Set() });
    setSearch('');
  };

  const activeFilterCount = filters.regions.size + filters.styles.size + filters.languages.size + (search ? 1 : 0);

  return (
    <div className="min-h-screen flex flex-col bg-bg">
      {/* Nav */}
      <NavBar
        variant="light"
        links={[
          { label: 'Drawing Events', href: '/events' },
          { label: 'Artists', href: '/artists' },
          { label: 'Models', href: '/models', active: true },
        ]}
      />

      {/* Header */}
      <section className="pt-[88px] pb-10">
        <div className="page-container">
          <div className="page-eyebrow">
            The roster
          </div>

          <div className="page-header">
            <h1 className="page-heading">
              Find a model
            </h1>
          </div>

          {/* Toolbar */}
          <div className="flex items-center gap-[18px] py-[18px] flex-wrap">
            <div className="flex-1 min-w-[240px] relative">
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-text-faint">
                <circle cx="7" cy="7" r="5"/>
                <path d="M11 11l3.5 3.5"/>
              </svg>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name..."
                className="w-full h-11 pl-10 pr-4 bg-transparent border border-line rounded text-[15px] text-text placeholder:text-text-faint placeholder:italic focus:border-text focus:outline-none transition-colors"
              />
            </div>
            <div className="text-[13.5px] text-text-dim italic">
              <span className="font-medium not-italic text-text">{sortedModels.length}</span> of {total} models
            </div>
            <div className="flex items-center gap-3 text-[13px] text-text-dim">
              <span>Sort</span>
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value as typeof sort)}
                className="h-9 px-3 pr-7 bg-transparent border border-line rounded text-sm text-text appearance-none cursor-pointer"
                style={{
                  backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'><path d='M3 5l3 3 3-3' stroke='%2315110d' stroke-width='1.2' fill='none' stroke-linecap='round' stroke-linejoin='round'/></svg>")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 8px center',
                }}
              >
                <option value="featured">Featured</option>
                <option value="recent">Recently active</option>
                <option value="alphabetical">Alphabetical</option>
              </select>
            </div>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="page-container">
        <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-14 pb-24 pt-9 items-start">
          {/* Filter Rail */}
          <aside className="lg:sticky lg:top-24">
            <div className="flex justify-between items-baseline pb-3.5 border-b border-line mb-[18px]">
              <h3 className="text-base font-normal">Filter</h3>
              {activeFilterCount > 0 && (
                <button
                  onClick={clearFilters}
                  className="bg-none border-none cursor-pointer font-display italic text-[12.5px] text-text-dim hover:text-text hover:underline"
                >
                  clear all
                </button>
              )}
            </div>

            {/* Region filter */}
            <FilterGroup label="Region">
              {Object.keys(REGIONS).map(region => (
                <FilterOption
                  key={region}
                  label={region}
                  count={regionCounts[region] || 0}
                  checked={filters.regions.has(region)}
                  onChange={() => toggleFilter('regions', region)}
                />
              ))}
            </FilterGroup>

            {/* Style filter */}
            <FilterGroup label="Style">
              {MODELING_STYLE_OPTIONS.map(option => (
                <FilterOption
                  key={option.value}
                  label={option.label}
                  count={styleCounts[option.value] || 0}
                  checked={filters.styles.has(option.value)}
                  onChange={() => toggleFilter('styles', option.value)}
                />
              ))}
            </FilterGroup>

            {/* Languages filter - only show languages that models speak */}
            {Object.keys(languageCounts).length > 0 && (
              <FilterGroup label="Languages">
                {Object.entries(languageCounts)
                  .sort((a, b) => b[1] - a[1]) // Sort by count descending
                  .map(([code]) => (
                    <FilterOption
                      key={code}
                      label={LANGUAGES[code as LanguageCode] || code}
                      count={languageCounts[code]}
                      checked={filters.languages.has(code as LanguageCode)}
                      onChange={() => toggleFilter('languages', code)}
                    />
                  ))}
              </FilterGroup>
            )}

          </aside>

          {/* Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-9 gap-x-7">
            {loading ? (
              // Loading skeleton
              Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-[4/5] bg-panel2 rounded" />
                  <div className="h-5 bg-panel2 rounded mt-4 w-3/4" />
                  <div className="h-4 bg-panel2 rounded mt-2 w-1/2" />
                </div>
              ))
            ) : sortedModels.length === 0 ? (
              <div className="col-span-full py-[72px] text-center">
                <h3 className="font-light text-[32px] text-text mb-2">No models match — yet.</h3>
                <p className="text-text-dim italic">Try removing a filter, or widening your search.</p>
              </div>
            ) : (
              sortedModels.map(model => (
                <ModelCard key={model.id} model={model} />
              ))
            )}
          </div>
        </div>
      </section>

      <SharedFooter />
    </div>
  );
}

function ModelCard({ model }: { model: ModelWithCount }) {
  const primaryPhoto = model.photos[0];
  const country = model.country ? getCountryName(model.country) : null;
  const firstName = model.displayName.split(' ')[0];
  const sessionCount = model.sessionCount || 0;

  // Build the meta text
  const metaParts: string[] = [];
  if (country) metaParts.push(country);
  if (sessionCount > 0) {
    metaParts.push(`${sessionCount} session${sessionCount === 1 ? '' : 's'}`);
  }

  return (
    <article className="flex flex-col text-inherit group">
      <div className="relative">
        <Link to={`/u/${model.username}`} className="block no-underline text-inherit">
          <div
            className="aspect-[4/5] bg-panel2 bg-cover bg-center relative overflow-hidden rounded transition-transform duration-[350ms] ease-out group-hover:scale-[1.012]"
            style={primaryPhoto ? { backgroundImage: `url(${primaryPhoto.url})` } : undefined}
          />
        </Link>
        <div className="absolute top-2.5 right-2.5 z-10">
          <FollowButton
            profileId={model.id}
          />
        </div>
      </div>
      <div className="flex justify-between items-baseline pt-3.5 pb-1.5 border-b border-line">
        <Link to={`/u/${model.username}`} className="no-underline text-inherit hover:text-accent transition-colors">
          <h3 className="text-[22px] font-normal">{firstName}.</h3>
        </Link>
        {metaParts.length > 0 && (
          <span className="text-[13px] text-text-dim">{metaParts.join(', ')}</span>
        )}
      </div>
    </article>
  );
}

function FilterGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="py-4 border-b border-line-soft last:border-b-0">
      <span className="block text-[10.5px] uppercase text-text-dim mb-3">
        {label}
      </span>
      <div className="flex flex-col gap-2">
        {children}
      </div>
    </div>
  );
}

function FilterOption({
  label,
  count,
  checked,
  onChange,
}: {
  label: string;
  count?: number;
  checked: boolean;
  onChange: () => void;
}) {
  return (
    <label className="flex items-center gap-2.5 text-sm text-text cursor-pointer select-none hover:text-text">
      <input type="checkbox" checked={checked} onChange={onChange} className="hidden" />
      <span
        className={`w-3.5 h-3.5 border rounded-sm flex items-center justify-center flex-shrink-0 transition-all ${
          checked ? 'border-text' : 'border-text-faint'
        }`}
      >
        {checked && <span className="w-1.5 h-1.5 bg-text" />}
      </span>
      <span className="flex-1">{label}</span>
      {count !== undefined && count > 0 && (
        <span className="text-xs text-text-faint tabular-nums">{count}</span>
      )}
    </label>
  );
}
